/**
 * @fileoverview Educational content — 4C/ID "supportive information" (theory
 * topics) and "part-task practice" (drills).
 *
 * Theory topics are rendered in the TheoryDrawer; each has a key that
 * missions reference via theoryRefs. Formula strings use LaTeX and are
 * rendered with the shared LatexRenderer.
 *
 * @module HydraulicBalancingTesting/content
 */

// ============================================================================
// THEORY TOPICS (supportive information)
// ============================================================================

export const THEORY_TOPICS = [
  {
    key: 'why-balance',
    title: 'Why balance at all?',
    blocks: [
      {
        type: 'text',
        text:
          'Water always takes the path of least resistance. In an unbalanced heating network the radiators close to the pump receive far more water than they need (overflow), while distant radiators starve. The classic symptoms:',
      },
      {
        type: 'list',
        items: [
          'Rooms near the pump overheat; the last room on the top floor never reaches its setpoint.',
          'Valves hiss and whistle (flow velocity too high in overflowing branches).',
          'The system is "fixed" by raising the supply temperature or pump speed — which wastes energy in every other room.',
          'The return temperature stays high because water races through radiators barely cooled.',
        ],
      },
      {
        type: 'text',
        text:
          'For heat pumps this is not cosmetic. A heat pump lives or dies by its temperatures: a high return temperature forces a high condensing temperature, and the COP falls roughly 2–3% per extra Kelvin of supply temperature. Hydraulic balancing is the cheapest COP upgrade that exists.',
      },
    ],
  },
  {
    key: 'pressure-flow',
    title: 'Pressure and flow in a network',
    blocks: [
      {
        type: 'text',
        text:
          'Every pipe, bend and valve resists flow. For turbulent flow the pressure loss grows with the SQUARE of the flow rate:',
      },
      { type: 'latex', latex: '$ \\Delta p = r \\cdot \\dot{V}^2 $' },
      {
        type: 'text',
        text:
          'The pump provides a head that falls as flow rises (pump curve); the network consumes head as flow rises (system curve). The system always operates where the two curves intersect.',
      },
      { type: 'diagram', diagram: 'pumpCurve' },
      {
        type: 'text',
        text:
          'Crucially, all branches share the same pump: throttling one branch raises the pressure available to every other branch. That is why balancing is iterative — each valve you touch shifts ALL the flows. (You can watch this live in the simulation.)',
      },
      {
        type: 'latex',
        latex: '$ \\Delta p_{available,room} = \\Delta p_{pump} - \\sum \\Delta p_{pipes\\ on\\ the\\ way} $',
      },
    ],
  },
  {
    key: 'valves-kv',
    title: 'Valves: Kv, presets and authority',
    blocks: [
      {
        type: 'text',
        text:
          'The flow coefficient Kv describes how much water passes a valve at 1 bar pressure drop:',
      },
      { type: 'latex', latex: '$ \\dot{V} \\,[m^3/h] = K_v \\cdot \\sqrt{\\Delta p \\,[bar]} $' },
      {
        type: 'text',
        text:
          'A presettable thermostatic valve has a hidden second restriction. The preset position (1, 2, … 7, N) limits the maximum Kv: position 1 ≈ 0.04 m³/h, position N ≈ 0.73 m³/h (typical 15 mm insert). The thermostatic head then modulates between closed and that limit. Balancing means choosing the preset so the branch resistance matches the calculated design flow.',
      },
      {
        type: 'text',
        text:
          'Valve AUTHORITY tells you whether the valve can actually control its branch: the share of the branch pressure drop that occurs across the valve itself.',
      },
      { type: 'latex', latex: '$ a = \\frac{\\Delta p_{valve}}{\\Delta p_{branch}} \\geq 0.3 \\ldots 0.5 $' },
      {
        type: 'text',
        text:
          'If the authority is too low, the valve is a passenger — pipe friction dictates the flow and the thermostatic head barely changes anything.',
      },
      { type: 'diagram', diagram: 'authority' },
    ],
  },
  {
    key: 'radiators-dt',
    title: 'Radiators, spread and ΔT',
    blocks: [
      {
        type: 'text',
        text:
          'A radiator gives off heat according to its mean over-temperature above the room (EN 442):',
      },
      { type: 'latex', latex: '$ \\dot{Q} = K \\cdot \\Delta T_m^{1.3}, \\quad \\Delta T_m = \\tfrac{T_{sup}+T_{ret}}{2} - T_{room} $' },
      {
        type: 'text',
        text:
          'The water side must hand over the same heat: Q̇ = ṁ·c·(T_sup − T_ret). These two equations meet at one return temperature — the simulation iterates exactly this balance.',
      },
      {
        type: 'text',
        text:
          'Doubling the flow does NOT double the heat output: at design flow a radiator already delivers most of its possible output. The extra water of an overflowing branch mostly rushes through and raises the return temperature. That is why halving an overflow barely cools the room — but massively helps the rest of the network.',
      },
      {
        type: 'text',
        text:
          'Heat-pump systems are designed with a small spread (typically 5 K, e.g. 45/40 °C) and correspondingly larger flows and radiators than old boiler systems (which used 75/65 °C, ΔT 50 K above room).',
      },
    ],
  },
  {
    key: 'heat-pumps',
    title: 'Heat pumps: COP, heating curve, minimum flow',
    blocks: [
      {
        type: 'text',
        text:
          'The theoretical maximum efficiency of a heat pump depends only on the temperature lift between the source (outdoor air) and the sink (supply water):',
      },
      { type: 'latex', latex: '$ COP = \\eta \\cdot \\frac{T_{cond}}{T_{cond} - T_{evap}} $' },
      {
        type: 'text',
        text:
          'Rule of thumb: every 1 K less supply temperature is worth about 2–3% COP. The weather-compensated HEATING CURVE exploits this: on mild days the building needs less heat, so the supply temperature is lowered automatically:',
      },
      { type: 'latex', latex: '$ T_{sup} = 20\\,°C + offset + slope \\cdot (20\\,°C - T_{outdoor}) $' },
      {
        type: 'text',
        text:
          'A balanced system lets you lower the whole curve: no room needs "emergency overheat" any more, because every room gets exactly its share. An unbalanced system forces you to set the curve for its WORST room — and everyone else pays.',
      },
      {
        type: 'list',
        items: [
          'Minimum flow: the condenser needs a minimum circulation (here 0.35 m³/h) or the heat pump trips. Beware when throttling many valves or reducing the pump head.',
          'Pump power: P ≈ V̇·Δp/η. Oversized pump heads burn electricity 24/7 and create valve noise.',
          'A low return temperature is the signature of a healthy, balanced heat-pump system.',
        ],
      },
    ],
  },
  {
    key: 'method-b',
    title: 'Verfahren A vs. Verfahren B',
    blocks: [
      {
        type: 'text',
        text:
          'In Germany, hydraulic balancing is documented in two quality levels (VdZ forms; required for subsidies and, for heat pumps, mandated as Method B by BEG funding rules):',
      },
      {
        type: 'list',
        items: [
          'Verfahren A (estimate): design flows are estimated from rough rules per radiator size. Quick, but inaccurate.',
          'Verfahren B (calculation): a room-by-room heat-load calculation (DIN EN 12831), radiator check, and computed design flow per room. The presets are then derived from flow and available pressure.',
        ],
      },
      {
        type: 'text',
        text: 'The Method B workflow — exactly what this simulation walks you through:',
      },
      {
        type: 'list',
        ordered: true,
        items: [
          'Calculate each room\'s heat load Q̇ (transmission + ventilation at design outdoor temperature).',
          'Check the radiator can deliver Q̇ at the planned supply/return temperatures.',
          'Compute the design flow: V̇ = Q̇ / (ρ·c·ΔT) — with a 5 K spread, V̇ [l/h] ≈ Q̇ [W] / 5.8.',
          'Choose each valve preset so the branch reaches its design flow at the available pressure.',
          'Set the pump head and the heating curve as LOW as the now-balanced system allows.',
        ],
      },
      { type: 'latex', latex: '$ \\dot{V} = \\frac{\\dot{Q}}{\\rho \\cdot c_p \\cdot \\Delta T} $' },
    ],
  },
];

export const getTheoryTopic = (key) => THEORY_TOPICS.find((t) => t.key === key) || null;

// ============================================================================
// DRILLS (part-task practice)
// ============================================================================

/**
 * Drill 1: design-flow & preset calculator.
 * Random heat load and available pressure; the learner computes the design
 * flow and picks a preset. Pure generator — UI lives in DrillsPanel.
 */
export const generatePresetDrill = (rng = Math.random) => {
  const load = 400 + Math.round(rng() * 18) * 100; // 400..2200 W
  const dpKPa = 8 + Math.round(rng() * 14);        // 8..22 kPa available
  // design flow at 5 K spread: V[l/h] = Q / (1.163 W/(l·h·K) · 5 K)
  const flowLph = load / (1.163 * 5);
  const flowM3h = flowLph / 1000;
  const requiredKv = flowM3h / Math.sqrt(dpKPa / 100);
  return { load, dpKPa, flowLph, requiredKv };
};

/** Tolerance check for the flow answer (step 1). */
export const checkFlowAnswer = (drill, answerLph) =>
  Math.abs(answerLph - drill.flowLph) / drill.flowLph <= 0.05;

/**
 * Tolerance check for the preset answer (step 2): the chosen Kv must produce
 * a flow within ±15% of design at the given pressure.
 */
export const checkPresetAnswer = (drill, chosenKv) => {
  const resulting = chosenKv * Math.sqrt(drill.dpKPa / 100) * 1000; // l/h
  return Math.abs(resulting - drill.flowLph) / drill.flowLph <= 0.15;
};

/** Drill 2: diagnosis flashcards (symptom → cause). */
export const FLASHCARDS = [
  {
    id: 'f1',
    symptom: 'The radiators next to the boiler room get scalding hot within minutes; the top-floor rooms stay lukewarm.',
    options: [
      'The heat pump is undersized',
      'Classic unbalanced network: near branches overflow, far branches starve',
      'The top-floor radiators are airlocked',
    ],
    correct: 1,
    explain: 'The textbook signature of a missing hydraulic balance — fix with presets, not with a bigger pump.',
  },
  {
    id: 'f2',
    symptom: 'After balancing, one valve whistles audibly at night when most thermostats are closed.',
    options: [
      'The pump head is fixed too high — Δp across the few open valves explodes',
      'The valve preset is too open',
      'The radiator is too large',
    ],
    correct: 0,
    explain: 'With a constant-speed pump, closing valves piles the full head onto the remaining open ones. Lower the head or use a Δp-controlled pump.',
  },
  {
    id: 'f3',
    symptom: 'A heat pump short-cycles and shows a flow error when many thermostatic heads close simultaneously.',
    options: [
      'The compressor is broken',
      'The minimum circulation flow is violated',
      'The supply temperature is too low',
    ],
    correct: 1,
    explain: 'Heat pumps need a guaranteed minimum flow through the condenser — provide an open reference room, overflow valve or buffer.',
  },
  {
    id: 'f4',
    symptom: 'The measured return temperature is only 3 K below supply although the spread was designed as 5 K, and the COP is poor.',
    options: [
      'Overflow somewhere: water passes radiators too fast and returns hot',
      'The outdoor sensor is defective',
      'The spread is irrelevant for COP',
    ],
    correct: 0,
    explain: 'A small spread at full load = too much flow. The hot return drags the condensing temperature (and the COP) down.',
  },
  {
    id: 'f5',
    symptom: 'A room never reaches its setpoint although its valve preset is fully open ("N") and the pump runs at maximum.',
    options: [
      'The branch has the least available pressure — other branches must be throttled instead',
      'Open the valve further',
      'Raise the room setpoint',
    ],
    correct: 0,
    explain: 'You cannot open beyond "N". The pressure for the starved branch must come from restricting the overflowing ones.',
  },
  {
    id: 'f6',
    symptom: 'After lowering the heating curve, exactly one room becomes too cold while all others remain fine.',
    options: [
      'The whole curve must go back up',
      'That room\'s radiator/flow reserve is the bottleneck — rebalance or enlarge it, then keep the low curve',
      'Heat pumps cannot run low curves',
    ],
    correct: 1,
    explain: 'Never let one weak room dictate the temperature level of the whole building — fix the bottleneck locally.',
  },
];
